import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import type { LottieRefCurrentProps } from "lottie-react";
import { ChevronDown } from "lucide-react";

import MatchingExercise from "./MatchingExercise";
import LottieAnimation from "./LottieAnimation";
import WordMatchingExercise from "./WordMatchingExercise";

// Firebase Storage
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { storage } from "../firebase"; // <- dopasuj ścieżkę
import BookmarkKidsAnimation from "../assets/bookmark.json";
import useCtx from "../hooks/useCts";

interface KidsBookViewerProps {
  book?: KidsBook;
  pageResolver?: (
    page: number,
    book?: KidsBook,
  ) => { unitIndex: number; lessonIndex: number } | null;
  selectedPage?: number; // 1-based
  onChangePage?: (next: number) => void;
  onOpenSaveBookmarkModal?: () => void;
}

const bookColors: Record<string, { from: string; to: string }> = {
  kids1: { from: "#512ba5", to: "#737bba" },
  kids2: { from: "#18964b", to: "#79cf55" },
  kids3: { from: "#035b74", to: "#0097b2" },
  kids4: { from: "#3c7bc0", to: "#4f8fbf" },
  kids5: { from: "#f36600", to: "#f08854" },
};

const getBookKey = (book?: KidsBook) => {
  const byId = (book?.id ?? "").toLowerCase();
  if (byId && bookColors[byId]) return byId;
  const byNum = `kids${book?.num ?? 1}`;
  if (bookColors[byNum]) return byNum;
  return "kids1";
};

// Foldery stron w Firebase Storage
const storagePageFolders: Record<number, string> = {
  1: "kids/book1/pages",
  2: "kids/book2/pages",
  3: "kids/book3/pages",
  4: "kids/book4/pages",
  5: "kids/book5/pages",
};

const STARTER_PAGES = 2;
const REVIEW_PAGES = 3;
const KIDS_PAGE_SCALE = 1.2;

type KidsUnitOption = {
  label: string;
  page: number;
};

const kidsBookSubtitles: Record<number, string> = {
  1: "Magic Village",
  2: "Jungle World",
  3: "Robo City",
  4: "Space Mission",
  5: "Time Travel",
};

const defaultKidsUnitOptions: KidsUnitOption[] = [
  { label: "Starter", page: 1 },
  { label: "Unit 2", page: 11 },
  { label: "Unit 3", page: 19 },
  { label: "Unit 4", page: 27 },
  { label: "Unit 5", page: 35 },
  { label: "Unit 6", page: 43 },
  { label: "Unit 7", page: 51 },
  { label: "Unit 8", page: 59 },
  { label: "Unit 9", page: 67 },
  { label: "Unit 10", page: 75 },
  { label: "Unit 11", page: 83 },
  { label: "Review", page: 91 },
];

const bookOneUnitOptions: KidsUnitOption[] = [
  { label: "Starter", page: 1 },
  { label: "Unit 2", page: 11 },
  { label: "Unit 3", page: 19 },
  { label: "Unit 4", page: 27 },
  { label: "Unit 5", page: 35 },
  { label: "Unit 6", page: 43 },
  { label: "Unit 7", page: 51 },
  { label: "Unit 8", page: 59 },
  { label: "Unit 9", page: 67 },
  { label: "Unit 10", page: 75 },
  { label: "Unit 11", page: 83 },
  { label: "Unit 12", page: 91 },
  { label: "Review", page: 92 },
];

const BlockedActionsHint = () => (
  // NEW: styl do blokady druku + globalne "anty-kopiuj"
  <style>{`
    /* Global: brak zaznaczania i kopiowania (poza polami input) */
    .anti-copy, .anti-copy *:not(input):not(textarea) {
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      user-select: none !important;
      -webkit-touch-callout: none !important;
    }
    .anti-copy img {
      -webkit-user-drag: none !important;
      user-drag: none !important;
      pointer-events: none; /* obraz nie łapie klików — overlay zbiera zdarzenia */
    }
    /* Brak druku */
    @media print {
      body * { display: none !important; }
      .no-print { display: none !important; }
    }
  `}</style>
);

const BookPDFKids: React.FC<KidsBookViewerProps> = ({
  book,
  pageResolver,
  selectedPage,
  onChangePage,
  onOpenSaveBookmarkModal,
}) => {
  const bookNum = book?.num ?? 1;
  const themeKey = getBookKey(book);
  const colors = bookColors[themeKey];

  const comingSoon = false;
  const isControlled =
    typeof selectedPage === "number" && typeof onChangePage === "function";

  // Stan
  const [numPages, setNumPages] = useState<number>(0);
  const [internalPageNumber, setInternalPageNumber] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<
    "Lesson" | "Exercise 1" | "Exercise 2"
  >("Lesson");
  const [bookmarkAnimationLoaded, setBookmarkAnimationLoaded] = useState(false);
  const bookmarkLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const shouldAnimateBookmarkRef = useRef(true);

  const showStaticBookmark = () => {
    const finalFrame = Math.max(
      (bookmarkLottieRef.current?.getDuration(true) ?? 1) - 1,
      0,
    );

    bookmarkLottieRef.current?.stop();
    bookmarkLottieRef.current?.goToAndStop(finalFrame, true);
  };

  const pageNumber = isControlled
    ? Math.max(1, selectedPage ?? 1)
    : internalPageNumber;

  const setPageNumber = useCallback(
    (next: number | ((prev: number) => number)) => {
      const currentValue = isControlled
        ? Math.max(1, selectedPage ?? 1)
        : internalPageNumber;

      const resolvedNext =
        typeof next === "function" ? next(currentValue) : next;

      const safeNext = Math.max(1, resolvedNext);

      if (isControlled) {
        onChangePage?.(safeNext);
      } else {
        setInternalPageNumber(safeNext);
      }
    },
    [isControlled, onChangePage, selectedPage, internalPageNumber],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const pageWrapperRef = useRef<HTMLDivElement | null>(null);
  const [viewerHeight, setViewerHeight] = useState<number | null>(null);

  const [imgUrlCache, setImgUrlCache] = useState<Record<number, string>>({});
  const [isLoadingImg, setIsLoadingImg] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const imgRef = useRef<HTMLImageElement | null>(null);

  const headerTitle = useMemo(() => `English Unlocked ${bookNum}`, [bookNum]);
  const headerSubtitle = useMemo(
    () => kidsBookSubtitles[bookNum] ?? "",
    [bookNum],
  );
  const unitOptions = useMemo(
    () => (bookNum === 1 ? bookOneUnitOptions : defaultKidsUnitOptions),
    [bookNum],
  );
  const selectedUnitPage = useMemo(() => {
    let currentPageStart = unitOptions[0]?.page ?? 1;

    for (const option of unitOptions) {
      if (pageNumber >= option.page) {
        currentPageStart = option.page;
      } else {
        break;
      }
    }

    return currentPageStart;
  }, [pageNumber, unitOptions]);
  const ctx = useCtx();
  const userClient = ctx.clients.find(
    (client) => client.authId === ctx.user.uid,
  );
  const isAnglomaniaAdmin =
    userClient?.adminEmail === "biuro@anglomania.com.pl";

  // NEW: blokada skrótów klawiszowych, kopiowania i menu kontekstowego
  const keyGuard = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase();
    const ctrl = e.ctrlKey || e.metaKey;

    // Blokuj najpopularniejsze “wycieki”
    if (
      (ctrl && ["p", "s", "c", "x", "u", "a"].includes(key)) || // druk, zapisz, kopiuj, wytnij, źródło, zaznacz wszystko
      (ctrl && e.shiftKey && ["i", "c", "j"].includes(key)) || // devtools, console
      key === "printscreen" // PrintScreen (niezawodne to nie jest)
    ) {
      e.preventDefault();
      e.stopPropagation();
      // Dodatkowo: nadpisz schowek, jeśli możemy (nie zawsze zadziała)
      if (navigator.clipboard && key !== "printscreen") {
        try {
          navigator.clipboard.writeText("");
        } catch {}
      }
      return false;
    }
  }, []);

  const copyGuard = useCallback((e: ClipboardEvent) => {
    e.preventDefault();
    if (e.clipboardData) e.clipboardData.setData("text/plain", "");
  }, []);

  const contextGuard = useCallback((e: MouseEvent) => {
    e.preventDefault();
  }, []);

  const dragGuard = useCallback((e: DragEvent) => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    const node = containerRef.current ?? document;
    document.addEventListener("keydown", keyGuard, { capture: true });
    node.addEventListener("copy", copyGuard as any, { capture: true });
    node.addEventListener("contextmenu", contextGuard as any, {
      capture: true,
    });
    node.addEventListener("dragstart", dragGuard as any, { capture: true });

    return () => {
      document.removeEventListener("keydown", keyGuard, {
        capture: true,
      } as any);
      node.removeEventListener(
        "copy",
        copyGuard as any,
        { capture: true } as any,
      );
      node.removeEventListener(
        "contextmenu",
        contextGuard as any,
        { capture: true } as any,
      );
      node.removeEventListener(
        "dragstart",
        dragGuard as any,
        { capture: true } as any,
      );
    };
  }, [keyGuard, copyGuard, contextGuard, dragGuard]);

  // Responsywna szerokość
  useEffect(() => {
    const handleResize = () => {
      const w = containerRef.current?.clientWidth ?? 800;
      setContainerWidth(Math.max(320, Math.min(1000, w - 48)));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Załaduj listę stron
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      setActiveTab("Lesson");
      setViewerHeight(null);
      if (!isControlled) {
        setInternalPageNumber(1);
      }
      setNumPages(0);
      setImgUrlCache({});
      setLoadError(null);

      const folderPath = storagePageFolders[bookNum];

      if (!folderPath) {
        setLoadError("Nie znaleziono folderu stron dla tej książki.");
        return;
      }

      try {
        const folderRef = ref(storage, folderPath);
        const res = await listAll(folderRef);
        const pageNames = res.items
          .map((it) => it.name)
          .filter((n) => /\d+\.(jpg|jpeg|png|webp)$/i.test(n))
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

        if (!mounted) return;

        setNumPages(pageNames.length);
        if (pageNames.length === 0)
          setLoadError("Błąd wczytywania. Prosimy o kontakt z nami.");
      } catch (e) {
        console.error("Błąd listAll:", e);
        if (mounted)
          setLoadError("Nie udało się pobrać listy stron z Firebase Storage.");
      }
    };

    init();
    return () => {
      mounted = false;
    };
  }, [bookNum, isControlled]);

  useEffect(() => {
    if (!numPages) return;

    if (pageNumber > numPages) {
      setPageNumber(numPages);
      return;
    }

    if (pageNumber < 1) {
      setPageNumber(1);
    }
  }, [numPages, pageNumber, setPageNumber]);

  useEffect(() => {
    shouldAnimateBookmarkRef.current = true;
    bookmarkLottieRef.current?.stop();
    bookmarkLottieRef.current?.goToAndStop(0, true);
    setBookmarkAnimationLoaded(false);
  }, [book?.id, bookNum]);

  useEffect(() => {
    if (activeTab !== "Lesson") {
      shouldAnimateBookmarkRef.current = false;
      bookmarkLottieRef.current?.stop();
      return;
    }

    if (!bookmarkAnimationLoaded) return;

    if (!shouldAnimateBookmarkRef.current) {
      showStaticBookmark();
      return;
    }

    bookmarkLottieRef.current?.setSpeed(0.85);

    const timeoutId = window.setTimeout(() => {
      bookmarkLottieRef.current?.setSpeed(0.85);
      bookmarkLottieRef.current?.goToAndPlay(0, true);
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, bookmarkAnimationLoaded, book?.id, bookNum]);

  // Pobierz URL strony
  const fetchPageUrl = async (page: number): Promise<string> => {
    if (imgUrlCache[page]) return imgUrlCache[page];

    const folderPath = storagePageFolders[bookNum];
    const tryExts = ["jpg", "png", "webp", "jpeg"];

    for (const ext of tryExts) {
      try {
        const url = await getDownloadURL(
          ref(storage, `${folderPath}/${page}.${ext}`),
        );
        setImgUrlCache((prev) => ({ ...prev, [page]: url }));
        return url;
      } catch {
        // next ext
      }
    }
    throw new Error(`Nie znaleziono pliku dla strony ${page} w ${folderPath}`);
  };

  // Załaduj bieżącą stronę
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!numPages) return;
      setIsLoadingImg(true);
      setLoadError(null);
      setViewerHeight(null);

      try {
        await fetchPageUrl(pageNumber);
        if (!mounted) return;

        // prefetch sąsiadów
        const pre = [];
        if (pageNumber + 1 <= numPages) pre.push(pageNumber + 1);
        if (pageNumber - 1 >= 1) pre.push(pageNumber - 1);
        pre.forEach((p) => fetchPageUrl(p).catch(() => {}));

        setIsLoadingImg(false);
      } catch (e: any) {
        console.error(e);
        if (mounted) {
          setIsLoadingImg(false);
          setLoadError(e?.message ?? "Błąd ładowania strony.");
        }
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [pageNumber, numPages]);

  // Oblicz wysokość viewer'a
  const handleImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const targetWidth = Math.round(containerWidth * KIDS_PAGE_SCALE);
    const ratio = img.naturalHeight / img.naturalWidth;
    const cssHeight = Math.round(targetWidth * ratio);
    if (!viewerHeight || Math.abs(cssHeight - viewerHeight) > 2) {
      setViewerHeight(cssHeight);
    }
  };

  // Mapowanie strony -> lekcji
  const resolveForPage = useMemo(() => {
    if (pageResolver) return pageResolver;
    return (page: number, b?: KidsBook | null) => {
      const _book = b ?? book;
      if (!_book || !_book.units?.length) return null;

      let remaining = page;
      if (remaining <= STARTER_PAGES) return null;
      remaining -= STARTER_PAGES;

      for (let ui = 0; ui < _book.units.length; ui++) {
        const lessonsCount = _book.units[ui].lessons.length || 0;

        if (remaining <= lessonsCount) {
          const li = remaining - 1;
          return { unitIndex: ui, lessonIndex: li };
        }
        remaining -= lessonsCount;

        if (remaining <= REVIEW_PAGES) {
          return null; // review
        }
        remaining -= REVIEW_PAGES;
      }
      return null;
    };
  }, [pageResolver, book]);

  const currentLessonRef = useMemo(() => {
    const res = resolveForPage(pageNumber, book);
    if (!res) return null;
    const u = book?.units?.[res.unitIndex];
    const l = u?.lessons?.[res.lessonIndex];
    if (!u || !l) return null;
    return { ...res, unit: u, lesson: l };
  }, [pageNumber, book, resolveForPage]);

  // Nawigacja / zoom
  const canPrev = pageNumber > 1;
  const canNext = pageNumber < numPages;
  const goPrev = () => {
    if (activeTab !== "Lesson") {
      setActiveTab("Lesson");
      setPageNumber((p) => (p > 1 ? p - 1 : p));
      return;
    }

    setPageNumber((p) => (p > 1 ? p - 1 : p));
  };
  const goNext = () => {
    if (activeTab !== "Lesson") {
      setActiveTab("Lesson");
      setPageNumber((p) => (p < numPages ? p + 1 : p));
      return;
    }

    setPageNumber((p) => (p < numPages ? p + 1 : p));
  };

  const TabButton: React.FC<{
    k: "Lesson" | "Exercise 1" | "Exercise 2";
    disabled?: boolean;
  }> = ({ k, disabled }) => {
    const active = activeTab === k;
    return (
      <button
        onClick={() => !disabled && setActiveTab(k)}
        disabled={disabled}
        className={`py-1 text-right text-xs font-semibold
          ${active ? " border-zinc-200" : "bg-transparent border-transparent"}
          ${disabled ? "text-zinc-300 cursor-not-allowed" : ""}`}
        style={active ? { color: colors.from } : undefined}
      >
        {k === "Lesson" ? "Lesson" : k}
      </button>
    );
  };

  const showBookmarkLottie = activeTab === "Lesson";

  // === RENDER ===
  return (
    <div
      id="kids-book-page"
      className="kids-book-wrapper mt-6 w-full max-w-6xl mx-auto text-zinc-800 no-print"
    >
      <BlockedActionsHint /> {/* NEW: style z blokadami */}
      <div
        className="relative anti-copy" // NEW: klasa anti-copy
        ref={containerRef}
        // NEW: harden at container level
        onContextMenu={(e) => e.preventDefault()}
        style={{ overflowX: "clip" }}
      >
        {/* Pasek tytułu */}
        <div
          id="kids-book-header"
          className="sticky top-0 z-20 flex min-h-16 items-center justify-between gap-4 overflow-visible px-6 py-3 text-sm font-semibold text-white"
          style={{
            background: `linear-gradient(180deg, ${colors.from}, ${colors.to})`,
          }}
        >
          <button
            type="button"
            onClick={onOpenSaveBookmarkModal}
            className={`absolute left-5 top-full z-30 -translate-y-[30%] items-center justify-center ${
              showBookmarkLottie ? "flex" : "hidden"
            }`}
            aria-label="Zapisz zakładkę"
          >
            <LottieAnimation
              key={`kids-bookmark-${book?.id ?? bookNum}`}
              animationData={BookmarkKidsAnimation}
              autoplay={false}
              loop={false}
              className="h-16 w-16 -scale-x-100 sm:h-24 sm:w-24"
              lottieRef={bookmarkLottieRef}
              onDOMLoaded={() => {
                bookmarkLottieRef.current?.setSpeed(0.85);
                setBookmarkAnimationLoaded(true);
              }}
            />
          </button>

          <div className="relative z-20 min-w-0 select-none">
            <div className="tracking-wide">{headerTitle}</div>
            <div className="text-xs italic text-white/80">{headerSubtitle}</div>
          </div>
          <div className="relative z-20 flex shrink-0 items-center gap-4">
            <div className="hidden items-center gap-4 sm:flex">
              <button
                type="button"
                onClick={() => setActiveTab("Lesson")}
                className={`bg-transparent p-0 text-sm transition ${
                  activeTab === "Lesson"
                    ? "text-white underline underline-offset-4"
                    : "text-white/80 hover:text-white"
                }`}
              >
                Lesson
              </button>
              <button
                type="button"
                onClick={() => currentLessonRef && setActiveTab("Exercise 1")}
                disabled={!currentLessonRef}
                className={`bg-transparent p-0 text-sm transition ${
                  activeTab === "Exercise 1"
                    ? "text-white underline underline-offset-4"
                    : "text-white/80 hover:text-white"
                } disabled:cursor-not-allowed disabled:text-white/45`}
              >
                Exercise 1
              </button>
              <button
                type="button"
                onClick={() => currentLessonRef && setActiveTab("Exercise 2")}
                disabled={!currentLessonRef}
                className={`bg-transparent p-0 text-sm transition ${
                  activeTab === "Exercise 2"
                    ? "text-white underline underline-offset-4"
                    : "text-white/80 hover:text-white"
                } disabled:cursor-not-allowed disabled:text-white/45`}
              >
                Exercise 2
              </button>
            </div>
            <div className="relative w-[132px]">
              <select
                aria-label="Wybierz unit"
                value={selectedUnitPage}
                onChange={(e) => setPageNumber(Number(e.target.value))}
                className="w-full appearance-none rounded-full border border-white/50 bg-white/20 py-2 pl-4 pr-10 text-sm font-medium text-white shadow-sm backdrop-blur-sm outline-none"
              >
                {unitOptions.map((option) => (
                  <option
                    key={`${option.label}-${option.page}`}
                    value={option.page}
                    className="text-slate-900"
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                strokeWidth={2.3}
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/80"
              />
            </div>
          </div>
        </div>

        {/* Coming soon dla Kids 3 */}
        {comingSoon ? (
          <div className="p-10 flex items-center justify-center">
            <div className="text-center select-none">
              <div className="text-2xl font-extrabold mb-2">Coming soon</div>
              <p className="text-zinc-500">
                This book will be available here shortly.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Taby */}
            <div className="sm:hidden">
              <div className="flex justify-end gap-4 pr-4 py-1">
                <TabButton k="Lesson" />
                <TabButton k="Exercise 1" disabled={!currentLessonRef} />
                <TabButton k="Exercise 2" disabled={!currentLessonRef} />
              </div>
            </div>

            {/* Zawartość aktywnej zakładki */}
            <div
              style={{
                minHeight: viewerHeight
                  ? `${Math.round(viewerHeight)}px`
                  : "600px",
              }}
            >
              {(() => {
                switch (activeTab) {
                  case "Lesson":
                    return (
                      <div className="flex justify-center">
                        {isLoadingImg && !loadError && (
                          <div className="p-6 text-center text-zinc-500 select-none">
                            Ładowanie strony…
                          </div>
                        )}
                        {loadError && (
                          <div className="p-6 text-center text-red-600 select-none">
                            {loadError}
                          </div>
                        )}
                        {!isLoadingImg &&
                          !loadError &&
                          imgUrlCache[pageNumber] && (
                            <div
                              ref={pageWrapperRef}
                              className="w-full flex justify-center relative"
                              // NEW: dodatkowe zabezpieczenia
                              onContextMenu={(e) => e.preventDefault()}
                              onMouseDown={(e) => {
                                // blokuj zaznaczanie/przeciąganie
                                if (
                                  (e.target as HTMLElement)?.tagName !==
                                  "SELECT"
                                )
                                  e.preventDefault();
                              }}
                            >
                              {/* Obraz strony (nieinteraktywny) */}
                              <div
                                style={{
                                  width: Math.round(
                                    containerWidth * KIDS_PAGE_SCALE,
                                  ),
                                  maxWidth: "100%",
                                }}
                              >
                                <img
                                  ref={imgRef}
                                  src={imgUrlCache[pageNumber]}
                                  alt={`Page ${pageNumber}`}
                                  onLoad={handleImgLoad}
                                  draggable={false}
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                    userSelect: "none",
                                    pointerEvents: "none", // wszystkie eventy zbiera overlay
                                  }}
                                />
                              </div>

                              {/* NEW: overlay przechwytujący kliknięcia + watermark */}
                              <div
                                aria-hidden
                                className="absolute inset-0"
                                onContextMenu={(e) => e.preventDefault()}
                                onMouseDown={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                style={{ cursor: "default" }}
                              />
                            </div>
                          )}
                      </div>
                    );
                  case "Exercise 1":
                    return !currentLessonRef ? (
                      <div className="text-zinc-500 select-none">
                        Brak lekcji dla tej strony.
                      </div>
                    ) : (
                      <MatchingExercise
                        bookNum={bookNum}
                        unitIndex={currentLessonRef.unitIndex}
                        lessonIndex={currentLessonRef.lessonIndex}
                        story={currentLessonRef.lesson.story || []}
                        accentHex={colors.from}
                      />
                    );
                  case "Exercise 2":
                    return !currentLessonRef ? (
                      <div className="text-zinc-500 select-none">
                        Brak lekcji dla tej strony.
                      </div>
                    ) : (
                      <WordMatchingExercise
                        words={currentLessonRef.lesson.newWords || []}
                        accentHex={colors.from}
                      />
                    );

                  default:
                    return null;
                }
              })()}
              {/** === Przyciski kopiowania dla Anglomanii === */}
              {isAnglomaniaAdmin && currentLessonRef && (
                <div className="mt-8 text-center flex gap-4 justify-center">
                  <button
                    onClick={async () => {
                      try {
                        const allQA = (currentLessonRef.lesson.QA || [])
                          .map((qa) => `${qa.q}, ${qa.a}`)
                          .join("\n");
                        await navigator.clipboard.writeText(allQA);
                      } catch (err) {
                        console.error(
                          "Błąd przy kopiowaniu pytań i odpowiedzi",
                          err,
                        );
                      }
                    }}
                    className="px-4 py-2 rounded-lg shadow font-bold text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Anglomania: Kopiuj pytania i odpowiedzi
                  </button>

                  <button
                    onClick={async () => {
                      try {
                        const allWords = (
                          currentLessonRef.lesson.newWords || []
                        )
                          .map((w) => `${w.eng} — ${w.polish}`)
                          .join("\n");
                        await navigator.clipboard.writeText(allWords);
                      } catch (err) {
                        console.error("Błąd przy kopiowaniu słówek", err);
                      }
                    }}
                    className="px-4 py-2 rounded-lg shadow font-bold text-white bg-amber-600 hover:bg-amber-700"
                  >
                    Anglomania: Kopiuj słówka
                  </button>
                </div>
              )}
            </div>

            <div className="pointer-events-none fixed inset-x-0 bottom-4 z-30 flex justify-center px-4 sm:bottom-6">
              <div
                className="pointer-events-auto flex w-full items-center justify-between gap-2 rounded-full border border-white/70 p-2 shadow-lg shadow-slate-900/10 backdrop-blur-xl sm:w-[360px]"
                style={{ backgroundColor: `${colors.from}1A` }}
              >
                <button
                  type="button"
                  aria-label="Poprzednia strona"
                  onClick={goPrev}
                  disabled={!canPrev}
                  className="flex min-w-[92px] items-center justify-center rounded-full bg-white/35 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-white/55 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>

                <div className="flex min-w-0 items-center gap-2 rounded-full border border-white/50 bg-white/5 px-3 py-2 text-sm font-medium text-slate-700">
                  <span className="whitespace-nowrap">Page</span>
                  <select
                    value={pageNumber}
                    onChange={(e) => setPageNumber(Number(e.target.value))}
                    disabled={!numPages}
                    className="min-w-0 bg-transparent text-center outline-none"
                  >
                    {Array.from({ length: numPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  aria-label="Następna strona"
                  onClick={goNext}
                  disabled={!canNext}
                  className="flex min-w-[92px] items-center justify-center rounded-full bg-white/35 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-white/55 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookPDFKids;
